import { msalInstance } from '..';
import { getAuthHeader, signOut } from '../services/authHelper';
import { clearFmsToken } from '../auth/fmsToken';
import {
  appEventEmitter,
  AUTH_ERROR_EVENT,
  FETCH_ERROR_EVENT,
} from '../services/eventEmitter';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const isProtectedUrl = (url: string): boolean => !url.includes('/a/authorize');

let signOutInFlight = false;

export const postCsv = async <TResponse>(
  url: string,
  csvContent: string,
  abortController: AbortController
): Promise<TResponse> => {
  const headers = await getAuthHeader(url);
  headers.set('Content-Type', 'text/csv');

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: csvContent,
    signal: abortController.signal,
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`API Error: ${response.status} - ${errorBody}`);
  }

  return (await response.json()) as TResponse;
};

export const internalFetch = async (
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  abortController: AbortController,
  body?: string,
  noJsonResponse?: boolean,
  retry?: number,
  okStatuses?: number[]
): Promise<any> => {
  const buildOptions = async () => ({
    method,
    headers: await getAuthHeader(url),
    timeout: 3000,
    body,
    signal: abortController?.signal,
  });

  let options = await buildOptions();
  const signal = options.signal;
  let reExchanged = false;

  for (let attempt = 0; attempt < (retry ?? 1); attempt++) {
    try {
      const response = await fetch(url, options);
      if (response.ok || okStatuses?.includes(response.status)) {
        if (noJsonResponse) return response;
        const text = await response.text();
        if (!text) return undefined;
        try {
          return JSON.parse(text);
        } catch (parseError: any) {
          const message: string = parseError?.errorMessage || '';
          if (message.includes('AADSTS50058')) {
            throw new Error(
              'Authentication token expired, please sign in again'
            );
          }
          throw parseError;
        }
      } else {
        if (response.status === 401) {
          if (isProtectedUrl(url) && !reExchanged) {
            reExchanged = true;
            clearFmsToken();
            options = await buildOptions();
            attempt--;
            continue;
          }
          if (!signOutInFlight) {
            signOutInFlight = true;
            signOut(msalInstance, 'sessionExpired');
          }
          return Promise.reject({ message: 'Session expired' });
        }
        if (attempt === (retry ?? 0)) {
          let errorBody: unknown;
          try {
            errorBody = await response.json();
          } catch {
            errorBody = response.statusText;
          }
          const bodyText =
            errorBody && typeof errorBody === 'object'
              ? JSON.stringify(errorBody)
              : String(errorBody ?? response.statusText);
          const message = `API Error: ${response.status} - ${bodyText}`;
          appEventEmitter.emit(FETCH_ERROR_EVENT, message);
          return Promise.reject(new Error(message));
        }
        console.log(`response:${response.status} in attempt: ${attempt}`);
        if (response.status === 403) {
          appEventEmitter.emit(
            AUTH_ERROR_EVENT,
            "You don't have permission for this action."
          );
          return Promise.reject({
            message: "You don't have permission for this action.",
          });
        }
        await sleep(500);
      }
    } catch (error) {
      if (!signal.aborted) {
        if (attempt === (retry ?? 0)) {
          console.log(error);
          appEventEmitter.emit(FETCH_ERROR_EVENT, `API Error: ${error}`);
          return Promise.reject(error);
        }
        console.log(`fail in attempt: ${attempt}`);
        await sleep(500);
      }
    }
  }
};
